import React, { useCallback, useEffect, useMemo, useState } from "react";
import { sdh_iterationReport, sdh_refineCandidates, sdh_refineRequest, sdh_runRefinementJob,
         sdh_revertRefinement, sdh_remeasureRule,
         ASSESSMENT_BY_VALUE, GATE_TONE, SME_ASSESSMENTS,
         type IterationReport, type IterationItem, type RefineCandidates } from "../api/sdhAPI";

/**
 * "Iteration 2 — what changed" : the SME's second pass. Shows every rule the agent revised or dropped in
 * response to her feedback, with her original comment, the agent's explanation and a before/after diff.
 * She can filter by action / assessment and open each rule to confirm.
 */
export default function IterationTab(
  { entityId, appNm, iteration = 2, onOpen, onNote, sessionId }:
  { entityId: string; appNm: string; iteration?: number; onOpen: (ruleId: string) => void;
    onNote: (m: string) => void; sessionId?: string }
) {
  const [rep, setRep] = useState<IterationReport | null>(null);
  const [busy, setBusy] = useState(true);
  const [running, setRunning] = useState(false);
  const [fAction, setFAction] = useState<string | null>(null);
  const [fAssess, setFAssess] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [picker, setPicker] = useState(false);
  const [cand, setCand] = useState<RefineCandidates | null>(null);
  const [selAsm, setSelAsm] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setBusy(true);
    try { setRep(await sdh_iterationReport(entityId, iteration)); }
    catch { setRep(null); } finally { setBusy(false); }
  }, [entityId, iteration]);
  useEffect(() => { load(); }, [load]);

  // Open the picker (and load the counts) instead of firing the agent inline — running it inline is
  // what previously blocked every other API call.
  const openPicker = async () => {
    setPicker(true);
    try {
      const c = await sdh_refineCandidates(entityId);
      setCand(c);
      const pre: Record<string, boolean> = {};
      Object.keys(c.by_assessment || {}).forEach((a) => { pre[a] = String(a).startsWith("CONDITIONAL"); });
      setSelAsm(pre);
    } catch { setCand(null); }
  };
  const selectedCount = () => Object.entries(selAsm)
    .filter(([, v]) => v)
    .reduce((n, [a]) => n + ((cand?.by_assessment || {})[a] || 0), 0);

  const submitJob = async () => {
    const chosen = Object.entries(selAsm).filter(([, v]) => v).map(([a]) => a);
    if (!chosen.length) { onNote("Pick at least one decision category."); return; }
    setRunning(true);
    try {
      const saved = await sdh_refineRequest(entityId, appNm, undefined, chosen, undefined);
      const n = saved?.matching_rules ?? chosen.length;
      if (!n) { onNote("No rules match that selection — nothing was queued."); return; }
      const res: any = await sdh_runRefinementJob(sessionId || entityId, appNm, entityId);
      if (res?.status === "conflict") {
        onNote(`A pipeline is already running for this entity (process ${String(res.active_process_id || "").slice(0, 8)}…). ` +
               `Your selection is saved — queue the refinement once it finishes.`);
        return;
      }
      if (res?.success === false) { onNote(res?.message || "Could not queue the refinement job"); return; }
      setPicker(false);
      onNote(`Refinement queued for ${n} rule(s) — track it in Jobs; reopen this tab when it finishes.`);
    } catch (e: any) { onNote(e?.response?.data?.detail || "Could not queue the refinement job"); }
    finally { setRunning(false); }
  };

  const items = rep?.items || [];
  const shown = useMemo(() => items.filter((i) =>
    (!fAction || i.action === fAction) && (!fAssess || i.assessment === fAssess)), [items, fAction, fAssess]);

  if (busy) return <div className="it-empty">Loading iteration {iteration}…</div>;

  if (!items.length) {
    return (
      <div className="it">
        <style>{CSS}</style>
        <div className="it-empty">
          <b>No agent revisions yet.</b>
          <p>Send the rules you marked <i>Conditional</i> or <i>Rejected</i> back to the agent. It revises each one
             using your comment, the measured evidence and the governed values, then returns them here for
             your second pass.</p>
          <button className="btn go" disabled={running} onClick={openPicker}>
            {running ? "Queuing…" : "Choose rules to refine"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="it">
      <style>{CSS}</style>
      <div className="it-top">
        <div>
          <div className="h2">Agent revisions — iteration {rep?.iteration ?? iteration}</div>
          <div className="sub">
            {items.length} rule(s) the agent changed from your feedback. <b>This is a view, not a separate
            queue</b> — each rule also stays in <i>Review</i> under its decision, marked “✎ agent revised”.
            Confirm or discard the change here, then decide the rule as usual.
          </div>
        </div>
        <button className="btn go" disabled={running} onClick={openPicker}>{running ? "Queuing…" : "Refine more rules"}</button>
      </div>

      <div className="chips">
        {Object.entries(rep?.counts || {}).map(([a, n]) => (
          <button key={a} className={"chip a-" + String(a).toLowerCase() + (fAction === a ? " on" : "")}
            onClick={() => setFAction(fAction === a ? null : a)}>{a}<em>{n}</em></button>
        ))}
        <span className="sep" />
        {Object.entries(rep?.by_assessment || {}).map(([a, n]) => (
          <button key={a} className={"chip" + (fAssess === a ? " on" : "")}
            onClick={() => setFAssess(fAssess === a ? null : a)}>
            {ASSESSMENT_BY_VALUE[a]?.label || a}<em>{n}</em></button>
        ))}
        {(fAction || fAssess) && <button className="clr" onClick={() => { setFAction(null); setFAssess(null); }}>clear</button>}
      </div>

      {picker && (
        <div className="pk-scrim" onClick={() => setPicker(false)}>
          <div className="pk" onClick={(e) => e.stopPropagation()}>
            <header><b>Which rules should the agent refine?</b>
              <button className="x" onClick={() => setPicker(false)}>×</button></header>
            <div className="pk-body">
              {!cand ? <div className="mut">Loading…</div> : (
                <>
                  <p className="mut">{cand.total_refinable} rule(s) have SME feedback the agent can act on.
                    Pick the decision categories to include — this runs as a background job, so the app
                    stays usable.</p>
                  {(["CONDITIONAL", "REJECTED", "PENDING", "APPROVED"] as const).map((g) => {
                    const items = SME_ASSESSMENTS.filter((a) => a.gate === g && (cand.by_assessment || {})[a.value]);
                    if (!items.length) return null;
                    return (
                      <div className="pk-g" key={g}>
                        <div className="pk-gh" style={{ color: GATE_TONE[g] }}>{g}</div>
                        {items.map((a) => (
                          <label className="pk-o" key={a.value}>
                            <input type="checkbox" checked={!!selAsm[a.value]}
                              onChange={() => setSelAsm((m) => ({ ...m, [a.value]: !m[a.value] }))} />
                            <span>{a.label}</span>
                            <em>{(cand.by_assessment || {})[a.value]}</em>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <footer>
              <span className="mut">{selectedCount()} rule(s) selected</span>
              <span style={{ flex: 1 }} />
              <button className="btn ghost" onClick={() => setPicker(false)}>Cancel</button>
              <button className="btn go" disabled={running || !selectedCount()} onClick={submitJob}>
                {running ? "Queuing…" : `Queue refinement (${selectedCount()})`}</button>
            </footer>
          </div>
        </div>
      )}

      <div className="list">
        {shown.map((it: IterationItem) => {
          const isOpen = !!open[it.constraint_id];
          const chg = it.changes || {};
          const keys = Object.keys(chg);
          return (
            <div className="card" key={it.constraint_id}>
              <div className="chead" onClick={() => setOpen((o) => ({ ...o, [it.constraint_id]: !o[it.constraint_id] }))}>
                <span className={"act a-" + String(it.action || "").toLowerCase()}>{it.action}</span>
                <div className="nm">
                  <div className="t">{it.rule_name || it.constraint_id}</div>
                  <div className="f">{it.field || it.constraint_id}</div>
                </div>
                <span className="ver">v{it.version}</span>
                {it.review_status === "needs_re_review" && <span className="rr">↻ confirm</span>}
                <span className="cv">{isOpen ? "▾" : "▸"}</span>
              </div>
              {isOpen && (
                <div className="cbody">
                  {it.sme_comment && <div className="row"><span className="k">your comment</span><span className="v you">{it.sme_comment}</span></div>}
                  {it.explanation && <div className="row"><span className="k">agent</span><span className="v">{it.explanation}</span></div>}
                  {keys.length > 0 && (
                    <div className="diffs">
                      {keys.map((k) => (
                        <div className="diff" key={k}>
                          <div className="dk">{k.replace(/_/g, " ")}</div>
                          <div className="old"><span className="tag">before</span>{String(chg[k]?.from ?? "—")}</div>
                          <div className="new"><span className="tag">after</span>{String(chg[k]?.to ?? "—")}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="cacts">
                    <button className="btn ghost" onClick={() => onOpen(it.constraint_id)}>Open rule & decide</button>
                    <button className="btn ghost" title="Re-run this rule and store fresh numbers"
                      onClick={async () => {
                        try {
                          const r = await sdh_remeasureRule(entityId, appNm, it.constraint_id);
                          onNote(r?.status === "ok"
                            ? `Re-measured: flags ${r.violating} of ${r.applicable}.`
                            : `Could not re-measure (${r?.status || "error"}).`);
                          await load();
                        } catch { onNote("Re-measure failed"); }
                      }}>Re-measure</button>
                    <button className="btn undo" title="Discard this revision and restore the previous rule"
                      onClick={async () => {
                        if (!window.confirm(`Discard the agent revision for "${it.rule_name || it.constraint_id}" and restore the previous rule?`)) return;
                        try {
                          const r = await sdh_revertRefinement(entityId, appNm, it.constraint_id);
                          onNote(r?.status === "ok"
                            ? `Reverted — restored ${(r.restored_fields || []).join(", ") || "the rule"} (now v${r.version}).`
                            : `Nothing to revert (${r?.status}).`);
                          await load();
                        } catch { onNote("Revert failed"); }
                      }}>Discard revision</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && <div className="it-empty">No rules match this filter.</div>}
      </div>
    </div>
  );
}

const CSS = `
.pk-scrim{position:fixed;inset:0;z-index:90;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.pk{width:min(560px,94vw);max-height:86vh;background:#fff;border-radius:14px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,.28)}
.pk header{display:flex;align-items:center;gap:10px;padding:13px 16px;background:#0e2436;color:#fff;font-size:14px}
.pk header .x{all:unset;cursor:pointer;margin-left:auto;font-size:22px;color:#cdd8ea}
.pk-body{overflow:auto;padding:14px 16px}
.pk-g{margin-bottom:12px}
.pk-gh{font-size:10px;letter-spacing:.08em;font-weight:700;margin-bottom:5px}
.pk-o{display:flex;align-items:center;gap:9px;padding:6px 9px;border-radius:8px;cursor:pointer;font-size:13px}
.pk-o:hover{background:#f6f8fa}
.pk-o em{margin-left:auto;font-style:normal;font-family:ui-monospace,monospace;font-size:11px;color:#6b7c8d}
.pk footer{display:flex;align-items:center;gap:9px;padding:12px 16px;border-top:1px solid #e3e8ee;background:#f8fafb}
.it{--line:#e3e8ee;--muted:#6b7c8d;color:#0E2436}
.it-empty{padding:34px;text-align:center;color:var(--muted)}
.it-empty b{display:block;font-size:15px;color:#0E2436;margin-bottom:6px}
.it-empty p{max-width:620px;margin:0 auto 14px;font-size:13px;line-height:1.6}
.it-top{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:12px}
.it .h2{font-size:16px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.it .sub{font-size:12.5px;color:var(--muted);margin-top:2px}
.btn{all:unset;cursor:pointer;padding:8px 15px;border-radius:9px;font-size:13px;font-weight:600}
.btn.go{background:#0e7c7b;color:#fff}.btn.ghost{background:#eef1f5;color:#42586e}
.btn[disabled]{opacity:.55;cursor:not-allowed}
.chips{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:12px}
.chips .sep{width:1px;height:18px;background:var(--line);margin:0 4px}
.chip{all:unset;cursor:pointer;font-size:12px;padding:4px 11px;border-radius:20px;background:#f1f4f7;color:#4a5763;display:inline-flex;gap:6px}
.chip:hover{background:#e8edf2}.chip.on{background:#0e2436;color:#fff}
.chip em{font-style:normal;font-family:ui-monospace,monospace;font-size:10.5px;opacity:.75}
.clr{all:unset;cursor:pointer;font-size:12px;color:#0e7c7b}.clr:hover{text-decoration:underline}
.card{border:1px solid var(--line);border-radius:11px;margin-bottom:9px;background:#fff;overflow:hidden}
.chead{display:flex;align-items:center;gap:11px;padding:11px 13px;cursor:pointer}
.chead:hover{background:#f8fafb}
.act{font-size:10px;font-weight:700;border-radius:20px;padding:2px 9px;color:#fff}
.a-revise{background:#0e7c7b}.a-drop{background:#b23a30}.a-keep{background:#8a97a3}
.chead .nm{flex:1}.chead .t{font-size:13.5px;font-weight:600}
.chead .f{font-size:11px;color:var(--muted);font-family:ui-monospace,monospace}
.chead .ver{font-family:ui-monospace,monospace;font-size:11px;background:#eef1f5;color:#42586e;border-radius:6px;padding:1px 7px}
.chead .rr{font-size:10.5px;font-weight:600;background:#fbe4d6;color:#a5570f;border-radius:20px;padding:2px 8px}
.chead .cv{opacity:.45;font-size:10px}
.cbody{border-top:1px solid var(--line);padding:12px 13px;background:#fbfcfd}
.row{display:flex;gap:10px;margin-bottom:8px;font-size:12.5px}
.row .k{flex:0 0 92px;font-size:10px;letter-spacing:.07em;text-transform:uppercase;color:#9aa6b2;font-weight:700;padding-top:2px}
.row .v{flex:1;color:#33404e;line-height:1.5}
.row .v.you{color:#0b5f5e;background:#eef7f6;border-radius:7px;padding:6px 9px}
.diffs{display:flex;flex-direction:column;gap:9px;margin-top:10px}
.diff{border:1px solid var(--line);border-radius:9px;overflow:hidden}
.dk{font-size:10.5px;font-weight:700;color:#42586e;background:#f4f7fa;padding:5px 10px;text-transform:capitalize}
.old,.new{font-size:12px;padding:8px 10px;font-family:ui-monospace,monospace;line-height:1.5;white-space:pre-wrap;word-break:break-word}
.old{background:#fdf3f2;color:#8a3f38}.new{background:#f1faf5;color:#1d6b47;border-top:1px solid var(--line)}
.tag{display:inline-block;font-family:system-ui,sans-serif;font-size:9.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;margin-right:8px;opacity:.65}
.cacts{display:flex;justify-content:flex-end;gap:8px;margin-top:10px}
.btn.undo{background:#fdeceb;color:#b23a30}
.btn.undo:hover{background:#f8ddda}
`;
